import { API_BASE_URL as PUBLIC_ADMIN_BASE } from '@/config/api';
import { getAdminToken } from './auth';
// Admin panel should use the primary API base (/api/v1). Legacy /bck caused 404s for services.
// Prefer VITE_ADMIN_API_BASE override; otherwise default to API_BASE_URL (https://backend.doc.af/api/v1).
const API_BASE_URL = (import.meta.env.VITE_ADMIN_API_BASE as string) || PUBLIC_ADMIN_BASE;

export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  let token = getAdminToken();
  
  const makeRequest = async (authToken: string) => {
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    return response;
  };

  let response = await makeRequest(token || '');
  
  // If token expired, try to refresh
  if (response.status === 401 && token) {
    console.log('Token expired, attempting to refresh...');
    try {
      const refreshResponse = await fetch(`${API_BASE_URL}/admin/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        if (refreshData.success && refreshData.token) {
          // Update stored token
          const { setAdminToken } = await import('./auth');
          setAdminToken(refreshData.token);
          token = refreshData.token;
          
          // Retry original request with new token
          response = await makeRequest(token || '');
          console.log('Token refreshed successfully');
        }
      }
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      // Redirect to login if refresh fails
      window.location.href = '/admin/login';
    }
  }
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'API request failed');
  }

  return response.json();
};

export const apiRequestWithPagination = async <T = any>(
  endpoint: string,
  params: Record<string, any> = {}
): Promise<T> => {
  const token = getAdminToken();
  const searchParams = new URLSearchParams(params);
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}?${searchParams}`, config);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'API request failed');
  }

  return response.json();
};

export const uploadFile = async (file: File, endpoint: string): Promise<{ url: string }> => {
  const token = getAdminToken();
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Upload failed');
  }

  return response.json();
};
